import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import Button from '../../../../components/formControls/Button';
import FormSectionTitle from '../../../../components/FormSectionTitle';
import ToastMessage from '../../../../components/messages/ToastMessage';
import { saveVirtualOperatorTicketSettingsRequest } from '../../../../js/virtualOperator/actions';
import {
  isSaveTicketSettingsError,
  isSaveTicketSettingsLoaded,
  isSaveTicketSettingsSuccess,
} from '../../../../js/virtualOperator/selectors';
import TagsInput from '../../../../components/TagsInput';
import {
  getVirtualOperatorTicketAssignedGroups,
  getVirtualOperatorTicketAssignedUsers,
  getVirtualOperatorTicketAutoAssign,
  getVirtualOperatorTicketCategory,
  getVirtualOperatorTicketEmail,
} from '../../../../js/me/selectors';
import Select2 from '../../../../components/formControls/Select2';
import { FormGroup } from 'reactstrap';
import {
  getTicketCategories,
  getTicketCategoriesLoaded,
} from '../../../../js/tickets/selectors';
import Label from '../../../../components/formControls/Label';
import { getUserById, getUsers } from '../../../../js/users/selectors';
import { getGroupById, getGroups } from '../../../../js/groups/selectors';

const messages = defineMessages({
  title: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.title',
    defaultMessage: 'Ticket settings',
  },
  infoEmail: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.infoEmail',
    defaultMessage:
      'Write all email addresses that will receive an email when a ticket is inserted by virtual operator',
  },
  infoCategory: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.infoCategory',
    defaultMessage:
      'Select a category for tickets created by the virtual operator',
  },
  infoAssignment: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.infoAssignment',
    defaultMessage:
      'Select users or groups to whom tickets created by the virtual operator will be assigned',
  },
  emailPlaceholder: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.emailPlaceholder',
    defaultMessage: 'Add an email and press enter',
  },
  category: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.category',
    defaultMessage: 'Category',
  },
  assignTo: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.assignTo',
    defaultMessage: 'Assign to',
  },
  assignToUsers: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.assignToUsers',
    defaultMessage: 'Users',
  },
  assignToGroups: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.assignToGroups',
    defaultMessage: 'Groups',
  },
  addAutoAssign: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.addAutoAssign',
    defaultMessage: 'For lost calls, assign only to called user',
  },
  allUsersGroups: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.allUsersGroups',
    defaultMessage: 'All users',
  },
  buttonSave: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.button.save',
    defaultMessage: 'Save',
  },
  saveError: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.saveError',
    defaultMessage: 'Saving error',
  },
  saveSuccess: {
    id: 'PbxVirtualOperatorSettingsTicketSettings.saveSuccess',
    defaultMessage: 'Saving successfully performed',
  },
});

class PbxVirtualOperatorSettingsTicketSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      emails: this.props.emails || '',
      category: this.props.category
        ? {
            value: this.props.category,
            label: this.props.ticketCategories.find(
              (cat) => cat.id === this.props.category
            )?.name,
          }
        : null,
      users: this.props.assignedUsers
        ? this.props.assignedUsers.map((user) => ({
            value: user,
            label: this.props.userById(user).departmentFullname,
          }))
        : [],
      groups: this.props.assignedGroups
        ? this.props.assignedGroups.map((group) => ({
            value: group,
            label: this.props.groupById(group).name,
          }))
        : [],
      autoAssign: this.props.autoAssign || false,
    };
  }

  handleCategory = (selected) => {
    this.setState({
      category: selected,
    });
  };

  handleUsersAssignSelect = (selected) => {
    this.setState({
      users: selected,
    });
  };

  handleGroupsAssignSelect = (selected) => {
    this.setState({
      groups: selected,
    });
  };

  handleAutoAssign = (e) => {
    this.setState({
      autoAssign: e.target.checked,
    });
  };

  onChangeEmails = (emails) => {
    this.setState({ emails });
  };

  onSubmit = () => {
    const { emails, category } = this.state;
    this.props.save({
      emails,
      category: category ? category.value : null,
      users: this.state.users.map((user) => user.value),
      groups: this.state.groups.map((group) => group.value),
      autoAssign: this.state.autoAssign,
    });
  };

  render() {
    const {
      ticketCategories,
      users,
      groups,
      saving,
      savingError,
      savingSuccess,
      intl: { formatMessage },
    } = this.props;

    const categoryOptions = ticketCategories.map((element) => {
      return {
        value: element.id,
        label: element.name,
      };
    });

    const usersOptions =
      users &&
      users.map((user) => {
        return {
          value: user.id,
          label: user.departmentFullname,
        };
      });

    const groupsOptions =
      groups &&
      groups
        .filter((group) => !group.private)
        .map((group) => {
          return {
            value: group.id,
            label: group.main
              ? formatMessage(messages.allUsersGroups)
              : group.name,
          };
        });

    return (
      <>
        <FormSectionTitle className="mt-3 mb-1">
          {formatMessage(messages.title)}
        </FormSectionTitle>
        {savingError && (
          <ToastMessage
            type="danger"
            text={formatMessage(messages.saveError)}
            messageCode={savingError}
          />
        )}
        {savingSuccess && (
          <ToastMessage
            type="success"
            text={formatMessage(messages.saveSuccess)}
            messageCode={savingSuccess}
          />
        )}
        <Label className="px-3 mt-2" for="email">
          {formatMessage(messages.infoEmail)}
        </Label>
        <div className="row px-3">
          <div className="col-xl-4 col-md-6 col-sm-4">
            <FormGroup>
              <TagsInput
                pattern="email"
                value={this.state.emails}
                onChange={this.onChangeEmails}
                placeholder={formatMessage(messages.emailPlaceholder)}
              />
            </FormGroup>
          </div>
        </div>
        {ticketCategories && ticketCategories.length > 0 && (
          <div className="row px-3 mt-2">
            <div className="col-12 col-xl-6">
              <FormGroup>
                <Label for="category">
                  {formatMessage(messages.infoCategory)}
                </Label>
                <Select2
                  id="category"
                  name="category"
                  options={categoryOptions}
                  value={this.state.category || ''}
                  onChange={this.handleCategory}
                  isClearable
                />
              </FormGroup>
            </div>
          </div>
        )}
        <div className="row px-3 mt-2">
          <div className="col-12 col-xl-6">
            <FormGroup>
              <Label for="assignTo">
                {formatMessage(messages.infoAssignment)}
              </Label>
              <br />
              <Select2
                className="form-control p-0 d-inline-block col-5 col-xl-5"
                id="assignToUsers"
                name="assignToUsers"
                value={this.state.users}
                onChange={this.handleUsersAssignSelect}
                options={usersOptions}
                isClearable
                isMulti
                placeholder={formatMessage(messages.assignToUsers)}
              />
              <Select2
                className="form-control p-0 d-inline-block col-5 col-xl-5 ml-1"
                id="assignToGroups"
                name="assignToGroups"
                value={this.state.groups}
                onChange={this.handleGroupsAssignSelect}
                options={groupsOptions}
                isClearable
                isMulti
                placeholder={formatMessage(messages.assignToGroups)}
              />
            </FormGroup>
          </div>
          {
            <div className="col-12 col-xl-6 d-flex align-items-center">
              <div className="custom-control custom-checkbox custom-control-inline">
                <input
                  type="checkbox"
                  id="autoAssign"
                  name="autoAssign"
                  className="custom-control-input"
                  onChange={this.handleAutoAssign}
                  checked={this.state.autoAssign}
                />
                <label
                  className="custom-control-label yn-label"
                  htmlFor="autoAssign"
                >
                  {formatMessage(messages.addAutoAssign)}
                </label>
              </div>
            </div>
          }
        </div>
        <div className="pt-3 text-center">
          <Button
            className="btn btn-primary"
            loading={saving}
            text={formatMessage(messages.buttonSave)}
            onClick={this.onSubmit}
          />
        </div>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    ticketCategoriesLoaded: getTicketCategoriesLoaded(state),
    ticketCategories: getTicketCategories(state),
    category: getVirtualOperatorTicketCategory(state),
    emails: getVirtualOperatorTicketEmail(state),
    assignedUsers: getVirtualOperatorTicketAssignedUsers(state),
    assignedGroups: getVirtualOperatorTicketAssignedGroups(state),
    autoAssign: getVirtualOperatorTicketAutoAssign(state),
    users: getUsers(state),
    groups: getGroups(state),
    userById: (id) => getUserById(state, id),
    groupById: (id) => getGroupById(state, id),
    saving: !isSaveTicketSettingsLoaded(state),
    savingError: isSaveTicketSettingsError(state),
    savingSuccess: isSaveTicketSettingsSuccess(state),
  };
}

export default injectIntl(
  connect(mapStateToProps, {
    save: saveVirtualOperatorTicketSettingsRequest,
  })(PbxVirtualOperatorSettingsTicketSettings)
);
